import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { apiErrorMessage, useApiDocsManager, useRequireApiDocsManager, useSaveSheet, useSheet } from '../api';
import { MAX_SHEET_PAGES, sheetPages, type SheetPayload } from '../types';

const LIST = '/superadmin-api-docs/sheet-content';

const hasContent = (html: string) => html.replace(/<(?!img)[^>]*>/g, '').trim() !== '' || /<img/i.test(html);

export function SheetEditorPage() {
  useRequireApiDocsManager();
  const { canModify } = useApiDocsManager();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const existing = useSheet(id);
  const save = useSaveSheet();

  const [sheetName, setSheetName] = useState('');
  const [visibility, setVisibility] = useState<'ALL' | 'PRIVATE'>('ALL');
  const [pages, setPages] = useState<string[]>(['']);

  useEffect(() => {
    const s = existing.data;
    if (!s) return;
    if (!canModify(s.createdBy)) {
      toast.error('You can edit only the sheets you created');
      navigate(LIST, { replace: true });
      return;
    }
    setSheetName(s.sheetName);
    setVisibility(s.visibility === 'PRIVATE' ? 'PRIVATE' : 'ALL');
    const p = sheetPages(s);
    setPages(p.length ? p : ['']);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existing.data]);

  const modules = useMemo(
    () => ({
      toolbar: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link', 'image'],
        ['clean'],
      ],
    }),
    [],
  );

  const setPage = (i: number, value: string) => setPages((prev) => prev.map((p, idx) => (idx === i ? value : p)));
  const addPage = () => setPages((prev) => (prev.length < MAX_SHEET_PAGES ? [...prev, ''] : prev));
  const removePage = (i: number) => setPages((prev) => (prev.length > 1 ? prev.filter((_, idx) => idx !== i) : prev));

  const onSave = () => {
    if (!sheetName.trim()) return toast.error('Please enter a sheet name');
    const filled = pages.filter(hasContent);
    if (filled.length === 0) return toast.error('Please add some content');
    const payload: SheetPayload = {
      id: isEdit ? Number(id) : undefined,
      sheetName: sheetName.trim(),
      visibility,
      content1: filled[0] ?? null,
      content2: filled[1] ?? null,
      content3: filled[2] ?? null,
      content4: filled[3] ?? null,
      content5: filled[4] ?? null,
    };
    save.mutate(payload, {
      onSuccess: (r) => {
        toast.success(r.message);
        navigate(LIST);
      },
      onError: (e) => toast.error(apiErrorMessage(e, 'Could not save the sheet')),
    });
  };

  if (isEdit && existing.isLoading) {
    return (
      <ScrollPage>
        <div className="flex h-64 items-center justify-center gap-2 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" /> Loading sheet…
        </div>
      </ScrollPage>
    );
  }

  if (isEdit && existing.isError) {
    return (
      <ScrollPage>
        <p className="text-sm text-destructive">Sheet not found or you do not have access.</p>
      </ScrollPage>
    );
  }

  return (
    <ScrollPage>
      <PageHeader
        title={isEdit ? 'Edit Sheet' : 'Add Sheet'}
        description="Write the content your users will see in API Docs (up to 5 sheets)."
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate(LIST)} disabled={save.isPending}>
              Cancel
            </Button>
            <Button onClick={onSave} disabled={save.isPending}>
              {save.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              {isEdit ? 'Update' : 'Save'}
            </Button>
          </div>
        }
      />

      <div className="grid max-w-4xl gap-4">
        <div className="grid gap-1.5">
          <Label htmlFor="sheetName">Sheet Name</Label>
          <Input id="sheetName" value={sheetName} onChange={(e) => setSheetName(e.target.value)} maxLength={255} />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="visibility">Visibility</Label>
          <select
            id="visibility"
            value={visibility}
            onChange={(e) => setVisibility(e.target.value === 'PRIVATE' ? 'PRIVATE' : 'ALL')}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
          >
            <option value="ALL">Public</option>
            <option value="PRIVATE">Private</option>
          </select>
        </div>

        {pages.map((page, i) => (
          <div key={i} className="grid gap-1.5">
            <div className="flex items-center justify-between">
              <Label>Sheet {i + 1}</Label>
              {pages.length > 1 && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-destructive hover:text-destructive"
                  onClick={() => removePage(i)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Remove Sheet
                </Button>
              )}
            </div>
            <div className="[&_.ql-editor]:min-h-[160px]">
              <ReactQuill theme="snow" value={page} onChange={(v) => setPage(i, v)} modules={modules} />
            </div>
          </div>
        ))}

        {pages.length < MAX_SHEET_PAGES && (
          <div>
            <Button variant="outline" onClick={addPage}>
              <Plus className="h-4 w-4" />
              Add Sheet
            </Button>
          </div>
        )}
      </div>
    </ScrollPage>
  );
}
